import React, { useState, useEffect } from 'react';

const MONTHS = [
  { short: 'Led', full: 'Leden', idx: 0 },
  { short: 'Úno', full: 'Únor', idx: 1 },
  { short: 'Bře', full: 'Březen', idx: 2 },
  { short: 'Dub', full: 'Duben', idx: 3 },
  { short: 'Kvě', full: 'Květen', idx: 4 },
  { short: 'Čer', full: 'Červen', idx: 5 },
  { short: 'Čvc', full: 'Červenec', idx: 6 },
  { short: 'Srp', full: 'Srpen', idx: 7 },
  { short: 'Zář', full: 'Září', idx: 8 },
  { short: 'Říj', full: 'Říjen', idx: 9 },
  { short: 'Lis', full: 'Listopad', idx: 10 },
  { short: 'Pro', full: 'Prosinec', idx: 11 },
];

// Exported helper: "10-3" or "Říjen - Březen" → { from: 9, to: 2 }
export const parseMonthRange = (value: string): { from: number; to: number } | null => {
  if (!value) return null;
  const numMatch = value.match(/^(\d{1,2})\s*[-–]\s*(\d{1,2})$/);
  if (numMatch) {
    return { from: parseInt(numMatch[1]) - 1, to: parseInt(numMatch[2]) - 1 };
  }
  const singleNum = value.match(/^(\d{1,2})$/);
  if (singleNum) {
    const m = parseInt(singleNum[1]) - 1;
    return { from: m, to: m };
  }
  const parts = value.split(/\s*[-–]\s*/);
  const findIdx = (s: string) =>
    MONTHS.findIndex(
      (m) =>
        m.full.toLowerCase() === s.trim().toLowerCase() ||
        m.short.toLowerCase() === s.trim().toLowerCase(),
    );
  const from = findIdx(parts[0]);
  const to = parts.length > 1 ? findIdx(parts[1]) : from;
  if (from === -1) return null;
  return { from, to: to === -1 ? from : to };
};

// Exported helper: number months (1-based strings) to Czech range string
export const numericToMonthString = (value: string): string => {
  const parsed = parseMonthRange(value);
  if (!parsed) return value;
  const { from, to } = parsed;
  if (from === to) return MONTHS[from]?.full ?? value;
  return `${MONTHS[from]?.full ?? ''} - ${MONTHS[to]?.full ?? ''}`;
};

const formatOutput = (from: number, to: number): string => {
  if (from === to) return MONTHS[from].full;
  return `${MONTHS[from].full} - ${MONTHS[to].full}`;
};

/**
 * isInRange: supports cross-year ranges.
 * E.g. from=9 (Oct) to=2 (Mar): months 9,10,11,0,1,2 are selected.
 */
const isInRange = (idx: number, from: number, to: number): boolean => {
  if (from <= to) return idx >= from && idx <= to;
  // cross-year: wraps around December→January
  return idx >= from || idx <= to;
};

interface MonthRangePickerProps {
  value?: string;
  onChange: (value: string) => void;
  className?: string;
}

export const MonthRangePicker: React.FC<MonthRangePickerProps> = ({ value = '', onChange, className }) => {
  const [from, setFrom] = useState<number | null>(null);
  const [to, setTo] = useState<number | null>(null);
  // phase: 'start' = waiting for first click, 'end' = waiting for second click
  const [phase, setPhase] = useState<'start' | 'end'>('start');

  useEffect(() => {
    const parsed = parseMonthRange(value);
    if (parsed) {
      // Only reset phase if the external value actually changed to something different
      // than our internal from/to selection.
      const isInternalChange = from === parsed.from && to === parsed.to;
      
      if (!isInternalChange) {
        setFrom(parsed.from);
        setTo(parsed.to);
        setPhase('start');
      }
    } else {
      setFrom(null);
      setTo(null);
      setPhase('start');
    }
  }, [value]);

  const handleClick = (idx: number) => {
    if (phase === 'start') {
      setFrom(idx);
      setTo(idx);
      setPhase('end');
      onChange(MONTHS[idx].full);
    } else {
      // Second click: always accept — including cross-year
      const newFrom = from!;
      setTo(idx);
      setPhase('start');
      onChange(formatOutput(newFrom, idx));
    }
  };

  const label = () => {
    if (phase === 'end') return 'Klikněte pro konec období…';
    if (value) return value;
    return 'Kliknutí = začátek, druhé kliknutí = konec (přes rok OK)';
  };

  // Cross-year indicator
  const isCrossYear = from !== null && to !== null && from > to;

  return (
    <div className={`flex flex-col gap-2 ${className ?? ''}`}>
      <div className="grid grid-cols-6 gap-1">
        {MONTHS.map((m) => {
          const inRange = from !== null && to !== null && isInRange(m.idx, from, to);
          const isFrom = m.idx === from;
          const isTo = m.idx === to;
          const isHover = phase === 'end';

          return (
            <button
              key={m.short}
              type="button"
              onClick={() => handleClick(m.idx)}
              title={m.full}
              className={[
                'text-[11px] font-medium py-1.5 px-0.5 rounded transition-all',
                inRange
                  ? isCrossYear
                    ? 'bg-orange-500 text-white'
                    : 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700',
                isFrom || isTo ? 'ring-2 ring-white ring-offset-1 ring-offset-green-600 scale-105 z-10' : '',
                isHover && !inRange ? 'opacity-60' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {m.short}
            </button>
          );
        })}
      </div>
      <div className="flex items-center justify-between text-xs min-h-[1.25rem]">
        <span className={`${phase === 'end' ? 'text-orange-500 font-medium' : 'text-gray-400'}`}>
          {label()}
        </span>
        {isCrossYear && (
          <span className="text-[11px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full font-medium">
            přes rok
          </span>
        )}
        {(from !== null || value) && (
          <button
            type="button"
            onClick={() => { setFrom(null); setTo(null); setPhase('start'); onChange(''); }}
            className="text-[11px] text-gray-400 hover:text-red-500 transition-colors ml-2"
          >
            ✕ zrušit
          </button>
        )}
      </div>
    </div>
  );
};
